<template>
  <base-modal body_class="pb-0">
    <z-form ref="form" @submit.native.prevent="on_submit">
      <z-form-group class="row">
        <z-form-input class="col-12" :rules="{ phone: true, required: true }" vid="number">
          <tel-input v-model="form.number" />
        </z-form-input>
      </z-form-group>

      <transition name="fade">
        <z-form-group v-if="step === steps.confirm" class="row">
          <div class="col-2">{{ $t('phone-number.number.modal.add_custom.field.code.title') }}</div>
          <z-form-input ref="code" class="col-10" vid="code" :rules="{ required: true }">
            <i-input v-model="form.code" />
          </z-form-input>
        </z-form-group>
      </transition>
    </z-form>

    <template slot="footer-ok">
      <i-button v-if="step === steps.init" v-bind="button_default_props" @click="on_submit">
        {{ $t('shared.component.button.add') }}
      </i-button>

      <i-button v-if="step === steps.confirm" v-bind="button_default_props" @click="on_submit">
        {{ $t('phone-number.number.modal.add_custom.button.check') }}
      </i-button>
    </template>
  </base-modal>
</template>

<script>
  import has from 'lodash-es/has';
  import BaseModal from '@shared/components/modal/modal';
  import TelInput from '@shared/components/input/input-phone-number';
  import { replaceNumberOnlyDigits } from '@shared/helpers/replace';
  import { SmsVerificationService } from '@core/services/SmsVerificationService';
  import { PageServerValidationResponseMixin } from '@shared/mixins/page-server-validation-response';
  import { NumberService } from '@phone-number/services/NumberService';
  import { RECAPTCHA_SITE_KEY } from '@shared/helpers/env';
  import { captcha } from '@utils/captcha';
  import encrypt from '@utils/encrypt';

  const STEP_INIT = 'init';
  const STEP_CONFIRM = 'confirm';

  export default {
    components: {
      BaseModal,
      TelInput
    },

    mixins: [PageServerValidationResponseMixin],

    data: () => ({
      is_loading: false,
      step: STEP_INIT,
      recaptchaScript: null,

      form: {
        number: '',
        code: ''
      },

      steps: {
        init: STEP_INIT,
        confirm: STEP_CONFIRM
      }
    }),

    computed: {
      button_default_props: function () {
        return {
          type: 'success',
          size: 'large',
          loading: this.is_loading
        };
      }
    },

    mounted() {
      this.recaptchaScript = document.createElement('script');
      this.recaptchaScript.setAttribute(
        'src',
        `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
      );
      document.head.appendChild(this.recaptchaScript);
    },

    beforeDestroy() {
      this.recaptchaScript.remove();
    },

    methods: {
      on_submit() {
        this.$refs.form.validate().then((is_valid) => {
          const form = {
            number: replaceNumberOnlyDigits(this.form.number),
            code: this.form.code
          };

          if (is_valid) {
            if (this.step === this.steps.init) {
              return this.check_duplicates(form);
            } else if (this.step === this.steps.confirm) {
              return this.check_code(form);
            }
          }
        });
      },

      check_duplicates(form) {
        this.is_loading = true;

        NumberService.checkNumber(form.number)
          .then((response) => {
            console.debug(response);
            this.send_sms(form);
          })
          .catch((e) => {
            const errorMessage = this.$t('phone-number.number.modal.add_custom.error.duplicate');
            console.debug(e);
            this.$noty.error(errorMessage);
          })
          .finally(() => (this.is_loading = false));
      },

      send_sms: function (params) {

        SmsVerificationService.token().then((response) => {
          const csrf_token = response.headers['xsrf-token'];

          captcha('lk_number_verify', (token) => {
            this.is_loading = true;
            SmsVerificationService.send(encrypt(csrf_token), { ...params, token })
              .then((response) => {
                if (response.message === 'bad_request') {
                  this.$noty.error(this.$t('core.errors.captcha_bad_request'));
                } else {
                  this.step = this.steps.confirm;
                }
              })
              .catch((error) => {
                this.handler_error(error);
              })
              .finally(() => (this.is_loading = false));
          });
        });
      },

      check_code: function (params) {
        this.is_loading = true;
        SmsVerificationService.check(params)
          .then(() => this.confirm_number(params))
          .catch((error) => this.handler_error(error))
          .finally(() => (this.is_loading = false));
      },

      confirm_number: function (params) {
        this.is_loading = true;

        NumberService.addUserNumber(params)
          .then(() => this.$emit('success'))
          .catch((error) => {
            this.handler_error(error);
            this.step = this.steps.init;
            this.form.code = '';
            this.$refs.code.reset();
          })
          .finally(() => (this.is_loading = false));
      },

      handler_error(error) {
        if (has(error, 'message')) {
          this.$noty.new_server_error(error);
        } else if (has(error, 'response.data.code')) {
          this.handler_error_code(error.response.data.code);
        } else {
          this.$noty.server_error(error);
        }
      },

      handler_error_code(code) {
        const locale_key = `core.register.error.${code}`;
        this.$noty.error(this.$t(locale_key));
      }
    }
  };
</script>
